import { DailyTotals, FactorAnalysisResult, FactorKey, FactorRow, MetricPeriod } from '@/types'
import { PlanData } from '@/services/analyticsService'

const DAYS_IN_WINDOW = 10
const DAYS_IN_WEEK = 7

const FACTOR_ORDER: Record<FactorKey, number> = {
  leads: 0,
  cr1: 1,
  cr2: 2,
  cr3: 3,
  aov: 4
}

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

function safeDiv(numerator: number, denominator: number): number {
  if (!denominator) return 0
  return numerator / denominator
}

function clampNonNegative(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

function calcDaysLeftInPeriod(periodEnd: string, today: string): number {
  const end = parseLocalDate(periodEnd).getTime()
  const current = parseLocalDate(today).getTime()
  const diffDays = Math.floor((end - current) / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays + 1)
}

function calcElapsedDaysInPeriod(periodStart: string, periodEnd: string, today: string): number {
  const start = parseLocalDate(periodStart).getTime()
  const end = parseLocalDate(periodEnd).getTime()
  const current = parseLocalDate(today).getTime()
  const clampedCurrent = Math.max(start, Math.min(current, end))
  const diffDays = Math.floor((clampedCurrent - start) / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays + 1)
}

function calcPeriodLengthDays(periodStart: string, periodEnd: string): number {
  const start = parseLocalDate(periodStart).getTime()
  const end = parseLocalDate(periodEnd).getTime()
  const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays + 1)
}

interface BuildRowParams {
  factor: FactorKey
  currentValue: number
  planValue: number | null
  daysLeftInPeriod: number
  futureLeads: number
  cr1: number
  cr2: number
  cr3: number
  aov: number
}

function buildFactorRow(params: BuildRowParams): FactorRow {
  const {
    factor,
    currentValue,
    planValue,
    daysLeftInPeriod,
    futureLeads,
    cr1,
    cr2,
    cr3,
    aov
  } = params

  const absDelta = planValue === null ? 0 : Math.abs(planValue - currentValue)

  let expectedContributionByPlanCap = 0
  if (absDelta > 0) {
    if (factor === 'leads') {
      const deltaPerDay = absDelta / DAYS_IN_WEEK
      expectedContributionByPlanCap = deltaPerDay * daysLeftInPeriod * cr1 * cr2 * cr3 * aov
    }
    if (factor === 'cr1') {
      expectedContributionByPlanCap = futureLeads * absDelta * cr2 * cr3 * aov
    }
    if (factor === 'cr2') {
      expectedContributionByPlanCap = futureLeads * cr1 * absDelta * cr3 * aov
    }
    if (factor === 'cr3') {
      expectedContributionByPlanCap = futureLeads * cr1 * cr2 * absDelta * aov
    }
    if (factor === 'aov') {
      expectedContributionByPlanCap = futureLeads * cr1 * cr2 * cr3 * absDelta
    }
  }

  return {
    factor,
    currentValue,
    planValue,
    expectedContributionByPlanCap: clampNonNegative(expectedContributionByPlanCap),
    lostToDateAmount: null,
    priority: 0
  }
}

export interface CalculateFactorAnalysisParams {
  rollingDailyData: DailyTotals[]
  periodDailyData: DailyTotals[]
  currentPeriod: MetricPeriod | null
  planData: PlanData | null
  todayString?: string
}

export function calculateFactorAnalysis({
  rollingDailyData,
  periodDailyData,
  currentPeriod,
  planData,
  todayString
}: CalculateFactorAnalysisParams): FactorAnalysisResult | null {
  if (!currentPeriod) return null

  const today = todayString || formatLocalDate(new Date())
  const endDate = parseLocalDate(today)
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - (DAYS_IN_WINDOW - 1))
  const windowStart = formatLocalDate(startDate)
  const windowEnd = today

  const inWindow = rollingDailyData.filter((day) => day.metric_date >= windowStart && day.metric_date <= windowEnd)

  const L28 = inWindow.reduce((sum, day) => sum + (day.leads_count || 0), 0)
  const B28 = inWindow.reduce((sum, day) => sum + (day.appointments_count || 0), 0)
  const V28 = inWindow.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0)
  const S28 = inWindow.reduce(
    (sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0),
    0
  )
  const A28 = inWindow.reduce(
    (sum, day) =>
      sum +
      (day.new_sales_amount || 0) +
      (day.new_subscriptions_amount || 0),
    0
  )

  const leadsPerDay = safeDiv(L28, DAYS_IN_WINDOW)
  const leadsPerWeek = leadsPerDay * DAYS_IN_WEEK
  const cr1 = safeDiv(B28, L28)
  const cr2 = safeDiv(V28, B28)
  const cr3 = safeDiv(S28, V28)
  const aov = safeDiv(A28, S28)

  const daysLeftInPeriod = calcDaysLeftInPeriod(currentPeriod.period_end, today)
  const futureLeads = leadsPerDay * daysLeftInPeriod
  const futureRevenue = futureLeads * cr1 * cr2 * cr3 * aov

  const inPeriodToDate = periodDailyData.filter(
    (day) => day.metric_date >= currentPeriod.period_start && day.metric_date <= today
  )
  const leadsFactMTD = inPeriodToDate.reduce((sum, day) => sum + (day.leads_count || 0), 0)
  const bookingsFactMTD = inPeriodToDate.reduce((sum, day) => sum + (day.appointments_count || 0), 0)
  const visitsFactMTD = inPeriodToDate.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0)
  const salesFactMTD = inPeriodToDate.reduce(
    (sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0),
    0
  )
  const amountFactMTD = inPeriodToDate.reduce(
    (sum, day) =>
      sum +
      (day.new_sales_amount || 0) +
      (day.new_subscriptions_amount || 0),
    0
  )

  const cr1FactMTD = safeDiv(bookingsFactMTD, leadsFactMTD)
  const cr2FactMTD = safeDiv(visitsFactMTD, bookingsFactMTD)
  const cr3FactMTD = safeDiv(salesFactMTD, visitsFactMTD)
  const aovFactMTD = safeDiv(amountFactMTD, salesFactMTD)

  const factMTD = amountFactMTD

  const forecastPeriodEndNewRevenue = factMTD + futureRevenue
  const planPeriodNewRevenue =
    (planData?.new_sales_amount_plan || 0) +
    (planData?.new_subscriptions_amount_plan || 0)
  const gapToPlanAmount = Math.max(0, planPeriodNewRevenue - forecastPeriodEndNewRevenue)

  const planCr1 = planData?.booking_conversion_plan != null ? planData.booking_conversion_plan / 100 : null
  const planCr2 = planData?.visit_conversion_plan != null ? planData.visit_conversion_plan / 100 : null
  const planCr3 = (() => {
    const visitorsPlan = planData?.new_visitors_count_plan || 0
    const totalNewSalesPlan = (planData?.new_sales_count_plan || 0) + (planData?.new_subscriptions_count_plan || 0)
    return visitorsPlan > 0 ? totalNewSalesPlan / visitorsPlan : null
  })()
  const planAov: number | null = (() => {
    const totalCountPlan = (planData?.new_sales_count_plan || 0) + (planData?.new_subscriptions_count_plan || 0)
    const totalAmountPlan =
      (planData?.new_sales_amount_plan || 0) +
      (planData?.new_subscriptions_amount_plan || 0)
    return totalCountPlan > 0 ? totalAmountPlan / totalCountPlan : null
  })()
  const elapsedDays = calcElapsedDaysInPeriod(currentPeriod.period_start, currentPeriod.period_end, today)
  const periodLenDays = calcPeriodLengthDays(currentPeriod.period_start, currentPeriod.period_end)
  const planLeadsPerWeek = planData
    ? Math.max(0, safeDiv((planData.leads_count_plan || 0), periodLenDays) * DAYS_IN_WEEK)
    : null
  const leadsPlanMTD = planData
    ? Math.max(0, (planData.leads_count_plan || 0) * (elapsedDays / periodLenDays))
    : null

  const planRevenueMTDModel = (
    leadsPlanMTD !== null &&
    planCr1 !== null &&
    planCr2 !== null &&
    planCr3 !== null &&
    planAov !== null
  )
    ? Math.max(0, leadsPlanMTD * planCr1 * planCr2 * planCr3 * planAov)
    : null

  const rows: FactorRow[] = [
    buildFactorRow({
      factor: 'leads',
      currentValue: leadsPerWeek,
      planValue: planLeadsPerWeek,
      daysLeftInPeriod,
      futureLeads,
      cr1,
      cr2,
      cr3,
      aov
    }),
    buildFactorRow({
      factor: 'cr1',
      currentValue: cr1,
      planValue: planCr1,
      daysLeftInPeriod,
      futureLeads,
      cr1,
      cr2,
      cr3,
      aov
    }),
    buildFactorRow({
      factor: 'cr2',
      currentValue: cr2,
      planValue: planCr2,
      daysLeftInPeriod,
      futureLeads,
      cr1,
      cr2,
      cr3,
      aov
    }),
    buildFactorRow({
      factor: 'cr3',
      currentValue: cr3,
      planValue: planCr3,
      daysLeftInPeriod,
      futureLeads,
      cr1,
      cr2,
      cr3,
      aov
    }),
    buildFactorRow({
      factor: 'aov',
      currentValue: aov,
      planValue: planAov,
      daysLeftInPeriod,
      futureLeads,
      cr1,
      cr2,
      cr3,
      aov
    })
  ]

  const hasEnoughBaseData = L28 > 0 && B28 > 0 && V28 > 0 && S28 > 0 && A28 > 0
  const factors = hasEnoughBaseData
    ? rows
        .slice()
        .sort((a, b) => {
          if (b.expectedContributionByPlanCap !== a.expectedContributionByPlanCap) {
            return b.expectedContributionByPlanCap - a.expectedContributionByPlanCap
          }
          return FACTOR_ORDER[a.factor] - FACTOR_ORDER[b.factor]
        })
        .map((row, index) => {
          let lostToDateAmount: number | null = null

          if (
            planRevenueMTDModel !== null &&
            leadsPlanMTD !== null &&
            planCr1 !== null &&
            planCr2 !== null &&
            planCr3 !== null &&
            planAov !== null
          ) {
            // Аддитивная цепная подстановка:
            // plan -> leads fact -> cr1 fact -> cr2 fact -> cr3 fact -> aov fact (= fact MTD)
            const step1 = leadsFactMTD * planCr1 * planCr2 * planCr3 * planAov
            const step2 = leadsFactMTD * cr1FactMTD * planCr2 * planCr3 * planAov
            const step3 = leadsFactMTD * cr1FactMTD * cr2FactMTD * planCr3 * planAov
            const step4 = leadsFactMTD * cr1FactMTD * cr2FactMTD * cr3FactMTD * planAov
            const factModel = leadsFactMTD * cr1FactMTD * cr2FactMTD * cr3FactMTD * aovFactMTD

            if (row.factor === 'leads') {
              lostToDateAmount = step1 - planRevenueMTDModel
            }
            if (row.factor === 'cr1') {
              lostToDateAmount = step2 - step1
            }
            if (row.factor === 'cr2') {
              lostToDateAmount = step3 - step2
            }
            if (row.factor === 'cr3') {
              lostToDateAmount = step4 - step3
            }
            if (row.factor === 'aov') {
              lostToDateAmount = factModel - step4
            }
          }

          return { ...row, lostToDateAmount, priority: index + 1 }
        })
    : []

  return {
    windowStart,
    windowEnd,
    daysInWindow: DAYS_IN_WINDOW,
    daysLeftInPeriod,
    planPeriodToDateNewRevenue: clampNonNegative(planRevenueMTDModel || 0),
    factPeriodToDateNewRevenue: clampNonNegative(factMTD),
    forecastPeriodEndNewRevenue: clampNonNegative(forecastPeriodEndNewRevenue),
    planPeriodNewRevenue: clampNonNegative(planPeriodNewRevenue),
    gapToPlanAmount: clampNonNegative(gapToPlanAmount),
    base: {
      leadsPerDay,
      cr1,
      cr2,
      cr3,
      aov
    },
    factors
  }
}
